import React from 'react';
import { Autocomplete, TextField } from '@mui/material';

/**
 * A wrapper around MUI Autocomplete that behaves like a Select.
 * 
 * @param {Array} options - Array of objects to select from.
 * @param {any} value - The current value (can be an ID or the full object).
 * @param {function} onChange - Callback fired when value changes (returns the ID by default).
 * @param {string} label - Label for the input.
 * @param {function} getOptionLabel - Function to determine the display text for an option.
 * @param {function} getOptionValue - Function to determine the underlying value (ID) of an option.
 */
export default function SearchableSelect({
  options = [],
  value,
  onChange,
  label,
  getOptionLabel = (option) => option ? `${option.code || ''} - ${option.nameAr || option.name || ''}` : '',
  getOptionValue = (option) => option?.id,
  error,
  helperText,
  disabled,
  ...props
}) {
  // Find the full object corresponding to the `value` prop (if `value` is an ID)
  const selectedOption = React.useMemo(() => {
    if (value === null || value === undefined || value === '') return null;
    // If value is already an object, return it; otherwise find it by ID
    return typeof value === 'object' 
      ? value 
      : options.find(opt => getOptionValue(opt) === value) || null;
  }, [value, options, getOptionValue]);

  return (
    <Autocomplete
      options={options}
      value={selectedOption}
      onChange={(event, newValue) => {
        // Return the underlying value (ID) to the parent's onChange handler
        onChange(newValue ? getOptionValue(newValue) : null);
      }}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, val) => {
        // Handle comparison whether val is the object or just the ID
        const valId = typeof val === 'object' ? getOptionValue(val) : val;
        return getOptionValue(option) === valId;
      }}
      renderOption={(props, option) => {
          return (
            <li {...props} key={option.id}>
              {getOptionLabel(option)}
            </li>
          );
      }}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          error={!!error}
          helperText={helperText}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      )}
      noOptionsText="لا يوجد نتائج"
      {...props}
    />
  );
}
